import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useTheme } from "../contexts/ThemeContext";
import { useLang } from "../contexts/LanguageContext";
import AppLayout from "../components/AppLayout";
import BottomNav from "../components/BottomNav";
import ReviewsSection from "../components/ReviewsSection";
import { AdBanner } from "../components/Ads";
import { Sparkles, Eye, Wind, Star, BookOpen, UserPlus, ChevronRight, Sun, Moon as MoonIcon, Globe, User, Scroll } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EDITORIAL = {
  fr: {
    title: "L'interprétation des rêves en Islam",
    author: "Par l'équipe DreamNoor — Sources : Ibn Sirin, An-Nabulsi, Al-Bukhari",
    paragraphs: [
      "L'interprétation des rêves (ta'bir al-ru'ya) est une science vénérée dans la tradition islamique. Le Prophète Muhammad ﷺ a dit : \"Les rêves véridiques sont une des quarante-six parties de la prophétie\" (rapporté par Al-Bukhari). Cette parole prophétique souligne l'importance spirituelle des rêves en Islam et leur lien avec la révélation divine.",
      "Depuis des siècles, les savants musulmans comme Ibn Sirin (654-728), considéré comme le père de l'interprétation des rêves en Islam, et An-Nabulsi ont développé des méthodologies rigoureuses pour décoder les symboles oniriques. Leur approche tient compte du contexte personnel du rêveur, de son état spirituel et des symboles universels décrits dans le Coran et la Sunna.",
      "DreamNoor s'inscrit dans cette tradition millénaire en combinant cette sagesse classique avec l'intelligence artificielle moderne. Notre technologie analyse les symboles de votre rêve — l'eau, les animaux, les personnes, les lieux — et les interprète à la lumière de la tradition islamique et de la psychologie contemporaine, pour vous offrir une lecture personnalisée et bienveillante.",
      "Que vous rêviez de serpents, d'eau, de vol ou de situations troublantes, chaque rêve porte un message. L'Islam distingue trois types de rêves : la vision véridique (ru'ya) venant d'Allah, le rêve de l'âme (hadith an-nafs) reflétant nos préoccupations, et le cauchemar venant du shaytan. Comprendre cette distinction est essentiel pour interpréter correctement ses rêves.",
    ],
  },
  en: {
    title: "Dream Interpretation in Islam",
    author: "By the DreamNoor team — Sources: Ibn Sirin, An-Nabulsi, Al-Bukhari",
    paragraphs: [
      "Dream interpretation (ta'bir al-ru'ya) is a venerated science in Islamic tradition. Prophet Muhammad ﷺ said: \"True dreams are one of the forty-six parts of prophethood\" (narrated by Al-Bukhari). This prophetic saying underscores the spiritual importance of dreams in Islam and their connection to divine revelation.",
      "For centuries, Muslim scholars like Ibn Sirin (654-728), considered the father of dream interpretation in Islam, and An-Nabulsi developed rigorous methodologies to decode dream symbols. Their approach considers the dreamer's personal context, spiritual state, and universal symbols described in the Quran and Sunnah.",
      "DreamNoor continues this thousand-year tradition by combining classical wisdom with modern artificial intelligence. Our technology analyzes your dream's symbols — water, animals, people, places — and interprets them in light of Islamic tradition and contemporary psychology, offering you a personalized and compassionate reading.",
      "Whether you dream of snakes, water, flying, or troubling situations, every dream carries a message. Islam distinguishes three types of dreams: the true vision (ru'ya) from Allah, the soul's dream (hadith an-nafs) reflecting our concerns, and the nightmare from shaytan. Understanding this distinction is essential for correct dream interpretation.",
    ],
  },
  es: {
    title: "La interpretación de los sueños en el Islam",
    author: "Por el equipo DreamNoor — Fuentes: Ibn Sirin, An-Nabulsi, Al-Bukhari",
    paragraphs: [
      "La interpretación de los sueños (ta'bir al-ru'ya) es una ciencia venerada en la tradición islámica. El Profeta Muhammad ﷺ dijo: \"Los sueños verdaderos son una de las cuarenta y seis partes de la profecía\" (narrado por Al-Bukhari). Esta palabra profética subraya la importancia espiritual de los sueños en el Islam.",
      "Durante siglos, eruditos musulmanes como Ibn Sirin (654-728), considerado el padre de la interpretación de sueños en el Islam, y An-Nabulsi desarrollaron metodologías rigurosas para descifrar los símbolos oníricos, teniendo en cuenta el contexto personal del soñador y los símbolos universales del Corán y la Sunna.",
      "DreamNoor se inscribe en esta tradición milenaria combinando sabiduría clásica con inteligencia artificial moderna. Nuestra tecnología analiza los símbolos de tu sueño y los interpreta a la luz de la tradición islámica y la psicología contemporánea.",
      "Ya sea que sueñes con serpientes, agua, volar o situaciones inquietantes, cada sueño lleva un mensaje. El Islam distingue tres tipos de sueños: la visión verdadera (ru'ya) de Allah, el sueño del alma (hadith an-nafs) y la pesadilla del shaytan.",
    ],
  },
  ar: {
    title: "تفسير الأحلام في الإسلام",
    author: "فريق دريم نور — المصادر: ابن سيرين، النابلسي، البخاري",
    paragraphs: [
      "تفسير الأحلام (تعبير الرؤيا) علم عريق في التراث الإسلامي. قال النبي محمد ﷺ: \"الرؤيا الصادقة جزء من ستة وأربعين جزءاً من النبوة\" (رواه البخاري). هذا الحديث الشريف يؤكد أهمية الأحلام الروحية في الإسلام وارتباطها بالوحي الإلهي.",
      "على مدى قرون، طور علماء المسلمين مثل ابن سيرين (654-728)، الذي يُعتبر أبا تفسير الأحلام في الإسلام، والنابلسي منهجيات دقيقة لفك رموز الأحلام. نهجهم يأخذ بعين الاعتبار السياق الشخصي للرائي وحالته الروحية والرموز الكونية المذكورة في القرآن والسنة.",
      "دريم نور يواصل هذا التراث العلمي الألفي بدمج الحكمة الكلاسيكية مع الذكاء الاصطناعي الحديث. تقنيتنا تحلل رموز حلمك — الماء والحيوانات والأشخاص والأماكن — وتفسرها على ضوء التراث الإسلامي وعلم النفس المعاصر.",
      "سواء حلمت بالثعابين أو الماء أو الطيران أو المواقف المقلقة، كل حلم يحمل رسالة. يميز الإسلام ثلاثة أنواع من الأحلام: الرؤيا الصادقة من الله، وحديث النفس الذي يعكس هموم الإنسان، والحلم من الشيطان.",
    ],
  },
};

const ARTICLES_LIST = [
  { slug: "signification-reve-serpent", fr: "Rêver d'un serpent en Islam", en: "Dreaming of a snake in Islam", es: "Soñar con serpiente en Islam", ar: "تفسير حلم الثعبان في الإسلام" },
  { slug: "signification-reve-mort", fr: "Rêver de la mort en Islam", en: "Dreaming of death in Islam", es: "Soñar con la muerte", ar: "تفسير حلم الموت" },
  { slug: "signification-reve-chat", fr: "Rêver d'un chat en Islam", en: "Dreaming of a cat in Islam", es: "Soñar con un gato", ar: "تفسير حلم القطة" },
  { slug: "signification-reve-eau", fr: "Rêver d'eau en Islam", en: "Dreaming of water in Islam", es: "Soñar con agua", ar: "تفسير حلم الماء" },
  { slug: "signification-reve-vol", fr: "Rêver de voler en Islam", en: "Dreaming of flying in Islam", es: "Soñar con volar", ar: "تفسير حلم الطيران" },
  { slug: "signification-reve-mariage", fr: "Rêver de mariage en Islam", en: "Dreaming of marriage", es: "Soñar con boda", ar: "تفسير حلم الزواج" },
  { slug: "signification-reve-bebe", fr: "Rêver d'un bébé en Islam", en: "Dreaming of a baby", es: "Soñar con un bebé", ar: "تفسير حلم الطفل" },
  { slug: "signification-reve-mer", fr: "Rêver de la mer en Islam", en: "Dreaming of the sea", es: "Soñar con el mar", ar: "تفسير حلم البحر" },
];

export default function HomePage() {
  const { user, isLoggedIn, api } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { t, lang, setLang, LANG_LIST, LANG_NAMES } = useLang();
  const isDark = theme === "dark";
  const navigate = useNavigate();
  const [inspiration, setInspiration] = useState(null);
  const [stats, setStats] = useState({ total_dreams: 0, today_dreams: 0 });
  const [showLangPicker, setShowLangPicker] = useState(false);

  useEffect(() => {
    axios.get(`${API}/inspiration?lang=${lang}`).then((r) => setInspiration(r.data)).catch(() => {});
    if (isLoggedIn) {
      api().get("/stats").then((r) => setStats(r.data)).catch(() => {});
    }
  }, [isLoggedIn, api, lang]);

  const textPrimary = isDark ? "text-white" : "text-slate-900";
  const textSecondary = isDark ? "text-slate-300/80" : "text-slate-600";
  const textMuted = isDark ? "text-slate-400" : "text-slate-500";
  const labelColor = isDark ? "text-amber-400" : "text-amber-600";
  const editorial = EDITORIAL[lang] || EDITORIAL.fr;

  return (
    <AppLayout>
      <div className="px-5 pt-12 pb-24 scrollbar-hide overflow-y-auto min-h-screen">
        {/* Header */}
        <div className="flex items-start justify-between animate-fadeInUp">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="DreamNoor" className="w-12 h-12" />
            <div>
              <p className={`${textSecondary} text-sm font-light`}>{isLoggedIn ? t("salam") : t("welcome")}</p>
              <h1 data-testid="home-greeting" className={`font-heading text-3xl font-bold ${textPrimary} ${isDark ? "drop-shadow-lg" : ""}`}>
                {isLoggedIn ? user?.name : t("appName")}
              </h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="lang-picker-toggle" onClick={() => setShowLangPicker(true)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-full transition-all active:scale-95 ${isDark ? "bg-white/10 hover:bg-white/15 text-slate-200" : "bg-black/5 hover:bg-black/10 text-slate-700"}`}>
              <Globe size={14} /><span className="text-[11px] font-semibold uppercase">{lang}</span>
            </button>
            <button data-testid="home-theme-toggle" onClick={toggleTheme}
              className={`p-2.5 rounded-full transition-all active:scale-90 ${isDark ? "bg-white/10 hover:bg-white/20 text-amber-400" : "bg-black/5 hover:bg-black/10 text-amber-600"}`}>
              {isDark ? <Sun size={16} /> : <MoonIcon size={16} />}
            </button>
          </div>
        </div>

        {/* Language modal */}
        {showLangPicker && (
          <div className="fixed inset-0 z-[100] flex items-end justify-center" onClick={() => setShowLangPicker(false)}>
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
            <div className="relative w-full max-w-md glass-card rounded-t-3xl rounded-b-none p-6 pb-10 animate-fadeInUp"
              onClick={(e) => e.stopPropagation()} style={{ background: isDark ? "rgba(15, 23, 42, 0.95)" : "rgba(255, 255, 255, 0.95)" }}>
              <div className="w-10 h-1 bg-slate-500/30 rounded-full mx-auto mb-5" />
              <p className={`text-[10px] ${labelColor} uppercase tracking-[0.2em] font-semibold mb-4`}>{t("language")}</p>
              <div className="grid grid-cols-2 gap-3">
                {LANG_LIST.map((l) => (
                  <button key={l} data-testid={`lang-${l}`} onClick={() => { setLang(l); setShowLangPicker(false); }}
                    className={`p-4 rounded-2xl text-center transition-all active:scale-95 ${lang === l ? "bg-amber-500/20 border-2 border-amber-500/50" : isDark ? "bg-white/5 border-2 border-transparent" : "bg-slate-100 border-2 border-transparent"}`}>
                    <p className={`text-lg font-bold ${lang === l ? (isDark ? "text-amber-400" : "text-amber-600") : textPrimary}`}>{LANG_NAMES[l]}</p>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Inspiration */}
        {inspiration && (
          <div data-testid="inspiration-card" className="mt-8 glass-card p-6 animate-fadeInUp relative overflow-hidden" style={{ animationDelay: "0.1s" }}>
            <div className="absolute top-3 right-3"><Star size={14} className={isDark ? "text-amber-400/60" : "text-amber-500/60"} /></div>
            <p className={`text-[10px] ${labelColor} uppercase tracking-[0.2em] font-semibold mb-3`}>{t("inspirationTitle")}</p>
            <p className={`font-heading text-lg font-semibold ${textPrimary} leading-relaxed italic`}>"{inspiration.text}"</p>
            <p className={`${textMuted} text-xs mt-3`}>{inspiration.source}</p>
          </div>
        )}

        {/* ══════ EDITORIAL SECTION (300+ words, BEFORE CTA) ══════ */}
        <div className="mt-8 glass-card p-6 animate-fadeInUp" style={{ animationDelay: "0.15s" }}>
          <div className="flex items-center gap-2 mb-1">
            <Scroll size={14} className={labelColor} />
            <h2 className={`font-heading text-xl font-bold ${textPrimary}`}>{editorial.title}</h2>
          </div>
          <p className={`text-[10px] ${textMuted} mb-4 flex items-center gap-1`}>
            <User size={10} /> {editorial.author}
          </p>
          <div className={`space-y-3 text-sm ${isDark ? "text-slate-300" : "text-slate-600"} leading-relaxed`}>
            {editorial.paragraphs.map((p, i) => <p key={i}>{p}</p>)}
          </div>
        </div>

        {/* CTA (after editorial content) */}
        <button data-testid="main-cta-interpret" onClick={() => navigate("/interpret")}
          className="mt-8 w-full btn-gold py-4 text-base font-semibold flex items-center justify-center gap-3 animate-fadeInUp" style={{ animationDelay: "0.2s" }}>
          <Sparkles size={20} /> {t("interpretDream")}
        </button>

        {/* Stats */}
        {isLoggedIn && (
          <div className="mt-6 grid grid-cols-2 gap-3 animate-fadeInUp" style={{ animationDelay: "0.25s" }}>
            <div className="glass-card p-4 text-center">
              <p className="font-heading text-3xl font-bold text-amber-500">{stats.total_dreams}</p>
              <p className={`${textMuted} text-[10px] mt-1 uppercase tracking-wider`}>{t("dreamsRecorded")}</p>
            </div>
            <div className="glass-card p-4 text-center">
              <p className="font-heading text-3xl font-bold text-purple-500">{stats.today_dreams}</p>
              <p className={`${textMuted} text-[10px] mt-1 uppercase tracking-wider`}>{t("today")}</p>
            </div>
          </div>
        )}

        {/* Quick Actions */}
        <div className="mt-6 animate-fadeInUp" style={{ animationDelay: "0.3s" }}>
          <p className={`text-[10px] ${textMuted} uppercase tracking-[0.2em] font-semibold mb-3`}>{t("explorer")}</p>
          <div className="grid grid-cols-3 gap-3">
            <button onClick={() => navigate("/signs")} className="glass-card p-4 flex flex-col items-center gap-2 hover:scale-[1.03] transition-all">
              <div className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center"><Eye size={18} className="text-blue-500" /></div>
              <span className={`text-[11px] ${textPrimary} font-medium`}>{t("signs")}</span>
            </button>
            <button onClick={() => navigate("/wellness")} className="glass-card p-4 flex flex-col items-center gap-2 hover:scale-[1.03] transition-all">
              <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center"><Wind size={18} className="text-emerald-500" /></div>
              <span className={`text-[11px] ${textPrimary} font-medium`}>{t("wellness")}</span>
            </button>
            <button onClick={() => navigate(isLoggedIn ? "/journal" : "/auth")} className="glass-card p-4 flex flex-col items-center gap-2 hover:scale-[1.03] transition-all">
              <div className="w-10 h-10 rounded-xl bg-purple-500/20 flex items-center justify-center"><BookOpen size={18} className="text-purple-500" /></div>
              <span className={`text-[11px] ${textPrimary} font-medium`}>{t("journal")}</span>
            </button>
          </div>
        </div>

        {/* Signup / Analysis */}
        {!isLoggedIn ? (
          <button onClick={() => navigate("/auth")}
            className="mt-6 w-full glass-card p-4 flex items-center justify-between group hover:scale-[1.01] transition-all animate-fadeInUp">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center"><UserPlus size={18} className="text-amber-500" /></div>
              <div className="text-left"><p className={`text-sm font-medium ${textPrimary}`}>{t("createAccount")}</p><p className={`text-[11px] ${textMuted}`}>{t("createAccountSub")}</p></div>
            </div>
            <ChevronRight size={16} className={textMuted} />
          </button>
        ) : (
          <button onClick={() => navigate("/analysis")}
            className="mt-6 w-full glass-card p-4 flex items-center justify-between group hover:scale-[1.01] transition-all animate-fadeInUp">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-pink-500/20 flex items-center justify-center"><Sparkles size={18} className="text-pink-500" /></div>
              <div className="text-left"><p className={`text-sm font-medium ${textPrimary}`}>{t("analyzeDreams")}</p><p className={`text-[11px] ${textMuted}`}>{t("analyzeDreamsSub")}</p></div>
            </div>
            <ChevronRight size={16} className={textMuted} />
          </button>
        )}

        {/* ══════ ARTICLES SECTION ══════ */}
        <div className="mt-8 animate-fadeInUp">
          <p className={`text-[10px] ${textMuted} uppercase tracking-[0.2em] font-semibold mb-3`}>
            {lang === "ar" ? "مقالات تفسير الأحلام" : lang === "es" ? "Artículos sobre sueños" : lang === "en" ? "Dream interpretation articles" : "Articles sur l'interprétation des rêves"}
          </p>
          <div className="space-y-2">
            {ARTICLES_LIST.map((a) => (
              <button key={a.slug} onClick={() => navigate(`/article/${a.slug}`)}
                className="w-full glass-card p-3.5 text-left hover:scale-[1.01] transition-all flex items-center justify-between">
                <span className={`text-sm font-medium ${textPrimary}`}>{a[lang] || a.fr}</span>
                <ChevronRight size={14} className={textMuted} />
              </button>
            ))}
          </div>
        </div>

        {/* Reviews */}
        <ReviewsSection />

        {/* About Section */}
        <div className="mt-8 glass-card p-5 animate-fadeInUp">
          <p className={`text-[10px] ${labelColor} uppercase tracking-[0.2em] font-semibold mb-3`}>
            {lang === "ar" ? "حول دريم نور" : lang === "es" ? "Sobre DreamNoor" : lang === "en" ? "About DreamNoor" : "À propos de DreamNoor"}
          </p>
          <p className={`text-sm ${isDark ? "text-slate-300" : "text-slate-600"} leading-relaxed`}>
            {lang === "ar"
              ? "دريم نور هو تطبيق متخصص في تفسير الأحلام يجمع بين الحكمة الإسلامية التقليدية والذكاء الاصطناعي الحديث. مصادرنا تشمل أعمال ابن سيرين والنابلسي والأحاديث النبوية الشريفة. نقدم تفسيرات شخصية ومجانية بأربع لغات."
              : lang === "es"
              ? "DreamNoor es una aplicación especializada en interpretación de sueños que combina la sabiduría islámica tradicional con inteligencia artificial moderna. Nuestras fuentes incluyen las obras de Ibn Sirin, An-Nabulsi y los hadices proféticos. Ofrecemos interpretaciones personalizadas y gratuitas en 4 idiomas."
              : lang === "en"
              ? "DreamNoor is a specialized dream interpretation application combining traditional Islamic wisdom with modern AI. Our sources include the works of Ibn Sirin, An-Nabulsi, and prophetic hadiths. We offer free, personalized interpretations in 4 languages."
              : "DreamNoor est une application spécialisée dans l'interprétation des rêves qui combine la sagesse islamique traditionnelle et l'intelligence artificielle moderne. Nos sources incluent les œuvres d'Ibn Sirin, d'An-Nabulsi et les hadiths prophétiques. Nous offrons des interprétations personnalisées et gratuites en 4 langues."}
          </p>
          <p className={`text-xs ${textMuted} mt-3`}>contact@dreamnoor.com — dreamnoor.com</p>
        </div>

        {/* Legal footer */}
        <div className="flex items-center justify-center gap-2 mt-8 mb-2 flex-wrap">
          {[
            { path: "/privacy", fr: "Confidentialité", en: "Privacy", es: "Privacidad", ar: "الخصوصية" },
            { path: "/terms", fr: "CGU", en: "Terms", es: "Términos", ar: "الشروط" },
            { path: "/legal", fr: "Mentions légales", en: "Legal", es: "Legal", ar: "قانوني" },
            { path: "/contact", fr: "Contact", en: "Contact", es: "Contacto", ar: "اتصل بنا" },
          ].map((link, i) => (
            <React.Fragment key={link.path}>
              {i > 0 && <span className={`text-[10px] ${isDark ? "text-slate-700" : "text-slate-300"}`}>|</span>}
              <button onClick={() => navigate(link.path)} className={`text-[10px] ${textMuted} underline underline-offset-2`}>
                {link[lang] || link.fr}
              </button>
            </React.Fragment>
          ))}
        </div>

        {/* Single ad — footer only, well separated from content */}
        <div className="mt-6 mb-4">
          <AdBanner slot="home-footer" />
        </div>
      </div>
      <BottomNav />
    </AppLayout>
  );
}
